/**
 * Education Strategy
 * 
 * This strategy handles the education topic, including analyzing education requirements
 * and providing recommendations.
 */

import { runAIMatches } from '../api';
import generateEnhancedGuidance from './enhancedGuidance';
import { createBaseStrategy, StrategyPhase } from './baseStrategy';

/**
 * Handler for the init phase of the education strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const initPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Education strategy: init phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nInitializing education discovery...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // Generate default guidance for education topic
  const defaultGuidance = `Focus on understanding the educational requirements for this role. Ask about degree requirements, certifications, or alternative qualifications that might be acceptable. Clarify if specific educational backgrounds are preferred or required.`;
  
  // Update decision to move to the suggestions phase
  return {
    ...decision,
    actionType: 'SEND_TO_BACKEND',
    agentGuidance: defaultGuidance,
    strategyPhase: StrategyPhase.SUGGESTIONS
  };
};

/**
 * Handler for the suggestions phase of the education strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const suggestionsPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Education strategy: suggestions phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating education suggestions...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Run AI matches to get education recommendations
    const aiMatchesResult = await runAIMatches(messages);
    
    if (!aiMatchesResult) {
      throw new Error('Failed to get AI matches');
    }
    
    // Parse icp_requirements from the AI matches result
    if (aiMatchesResult.search_gen_response && aiMatchesResult.search_gen_response.icp_requirements) {
      const icpRequirements = aiMatchesResult.search_gen_response.icp_requirements;
      
      // Log the icp_requirements for debugging
      console.log('Education ICP Requirements:', icpRequirements);
      
      // Check for education requirements
      const educationRequirements = icpRequirements.filter(req => 
        req.section_name.includes("Education") || 
        req.section_name.includes("Degree") ||
        req.section_name.includes("Certification")
      );
      
      if (educationRequirements.length > 0) {
        // Log education requirements
        console.log('Education Requirements:', educationRequirements);
        
        // Update thinking accordion with education requirements
        if (setMessages) {
          setMessages(prev => {
            const updatedMessages = prev.map(msg => 
              msg.role === 'AI' && msg.content === 'Typing...' 
                ? { 
                    ...msg, 
                    thinking: `${msg.thinking}\nFound education requirements: ${educationRequirements.map(req => req.bool_str).join(', ')}` 
                  }
                : msg
            );
            return updatedMessages;
          });
        }
      }
    }
    
    // Generate enhanced agent guidance with Findem Magic explanations
    const enhancedGuidance = await generateEnhancedGuidance(
      aiMatchesResult, 
      decision, 
      'education'
    );
    
    // Update decision with AI matches result and enhanced guidance
    return {
      ...decision,
      actionType: 'SEND_WITH_GUIDANCE',
      agentGuidance: enhancedGuidance,
      aiMatchesResult,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  } catch (error) {
    console.error('Error generating education suggestions:', error);
    
    // Fall back to conversation phase without suggestions
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Handler for the conversation phase of the education strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const conversationPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Education strategy: conversation phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nContinuing conversation about education...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // Generate specific guidance for the education conversation phase
  const educationConversationGuidance = `
IMPORTANT: The current topic is EDUCATION & CERTIFICATIONS. Focus ONLY on discussing education and certification requirements for this role.

1. First, acknowledge any education information the user has provided and ask for more details:
   - Are specific degrees required or preferred?
   - Are there any certifications that are important for this role?
   - Are there alternative qualifications that might be acceptable?
   - How important is formal education compared to practical experience?

2. Then, explore other aspects of education that might be relevant:
   - Are there specific educational backgrounds that tend to be successful in this role?
   - Are there any continuing education requirements?
   - Does the company support professional development or certification?

3. After gathering sufficient information, summarize the education requirements clearly:
   - "Based on our conversation, it sounds like you're looking for candidates with [summarize education requirements]..."

4. Finally, EXPLICITLY ask if they're ready to move on to the next topic:
   - "Does that summary of the education requirements look accurate? If so, we've covered all the main topics for this role."
   - "Have I captured your education needs correctly? If yes, we can wrap up our discussion."

DO NOT change the topic to discussing skills, experience, or any other topic until the user has EXPLICITLY confirmed they're ready to move on from the education discussion.
`;
  
  // In the conversation phase, we pass the user's message to the backend with specific education guidance
  return {
    ...decision,
    actionType: 'SEND_TO_BACKEND',
    agentGuidance: educationConversationGuidance,
    strategyPhase: StrategyPhase.CONVERSATION
  };
};

/**
 * Education strategy using the phase-based approach
 */
const educationStrategy = createBaseStrategy({
  topicName: 'education',
  initPhaseHandler,
  suggestionsPhaseHandler,
  conversationPhaseHandler
});

export default educationStrategy;
